// Booking widget: pick a court + sport + date + 1-hour slot, then continue to 365 to sign in + pay.
// Browse data is anonymous. The transactional step (login + Stripe + confirmation) happens FIRST-PARTY on
// the 365 platform: clicking Book redirects the whole page to {web}/embed-book with the selection, and the
// platform returns the customer here with ?booking=success. No cross-origin token/login lives in the widget.
import { Api } from '../api';
import { Auth, AuthRequiredError } from '../auth';
import type { BrowseVenue, BrowseZone, EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, money, renderError, renderLoading, footer, clear, receipt, signOutRow, stripQueryParam, showSalesPaused, blockIfSalesPaused, taxFor, totalWithTax, moneyPlusTax, taxNoteEl } from '../ui';
import { sitePage } from '../pages';
import { track, withWa } from '../analytics';

/**
 * The sales-tax rate to quote: the embed config's (always present for the widget's venue on a
 * current API), else the browse payload's, else 0 (older API → nothing tax-related renders).
 */
function taxPct(cfg: EmbedConfig, venue?: { salesTaxPercent?: number | null } | null): number {
  const v = cfg.salesTaxPercent ?? venue?.salesTaxPercent ?? 0;
  return Number(v) > 0 ? Number(v) : 0;
}

// Fallback sport list (values are SportType enum names the API accepts). The venue's own list now
// comes from the API (`allowedSports` on the browse-venue payload) so the widget shows exactly what
// 365sports.online shows; this shorter set is only used when that field is missing — an API that
// predates it, or a cached response.
const SPORTS = ['Basketball', 'Volleyball', 'Pickleball', 'Badminton', 'Other'];

// Customer-facing label for a SportType name (matches the platform's own formatting).
function sportLabel(name: string): string {
  return name === 'MultiSport' ? 'Multi-Sport' : name;
}
// Activity type is REQUIRED by the API. The venue's own list now comes from the API
// (`allowedActivityTypes` on the browse-venue payload) so the widget shows exactly what
// 365sports.online shows; this curated subset is only the fallback when that field is missing — an
// API that predates it, or a cached response. Feeds venue reporting + IoT automation.
const ACTIVITIES = ['Practice', 'Game', 'SkillsTraining', 'OpenGym', 'Pickup', 'Scrimmage', 'CampClinic', 'PrivateEvent'];

// Customer-facing label for an ActivityType name (matches the platform's own formatting).
function activityLabel(name: string): string {
  switch (name) {
    case 'SkillsTraining': return 'Skills Training';
    case 'OpenGym': return 'Open Gym / Open Play';
    case 'CampClinic': return 'Camp / Clinic';
    case 'TournamentSlot': return 'Tournament Slot';
    case 'PrivateEvent': return 'Private Event';
    default: return name;
  }
}

// The 365 platform base (web app) — derived from the hosted login URL the API hands us.
function platformBase(cfg: EmbedConfig): string {
  return (cfg.loginUrl || '').replace(/\/embed-login\.html.*$/, '').replace(/\/$/, '');
}

export function mountBooking(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  showSalesPaused(root, cfg);
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'booking') auth.clearPending();
  void load(root, cfg, api, auth);
}

async function load(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  if (!cfg.bookingEnabled) {
    renderError(root, 'Online booking isn’t available for this venue right now.');
    return;
  }

  // Login-first (venue owner's call): sign in BEFORE picking times or building an order. The sign-in
  // also establishes the 365 platform session (embed-login persists it), so checkout never asks again
  // — one login covers the venue site AND payment. Returning from the redirect lands back here
  // signed in and the full form renders.
  if (!auth.isLoggedIn) {
    clear(root);
    const signIn = h('button', { class: 'e365-btn' }, 'Sign in to book') as HTMLButtonElement;
    signIn.addEventListener('click', () => auth.redirectToLogin({ t: 'booking' }));
    root.append(
      h('h3', undefined, 'Book a court'),
      h('div', { class: 'e365-muted', style: 'margin:-4px 0 12px' },
        'Sign in to your 365 account to see times and book — one sign-in covers your whole checkout.'),
      signIn,
      footer(),
    );
    return;
  }

  renderLoading(root, 'Book a court');
  let venue: BrowseVenue;
  try {
    venue = await api.get<BrowseVenue>(`/api/browse/venues/${cfg.venueId}`);
  } catch {
    renderError(root, 'We couldn’t load availability. Please try again.', () => void load(root, cfg, api, auth));
    return;
  }

  const zones = (venue.zones || []).filter((z) => z.isAvailableForBooking !== false);
  if (zones.length === 0) {
    clear(root);
    root.append(h('h3', undefined, 'Book a court'), h('div', { class: 'e365-alert info' }, 'No bookable courts are listed yet.'), footer());
    return;
  }

  const sports = (venue.allowedSports && venue.allowedSports.length > 0) ? venue.allowedSports : SPORTS;
  const activities = (venue.allowedActivityTypes && venue.allowedActivityTypes.length > 0)
    ? venue.allowedActivityTypes : ACTIVITIES;
  // Sports the venue assigns courts for. EMPTY = off (the default and the overwhelmingly common case);
  // unlike the two lists above, an absent value must NOT be read as "all".
  renderForm(root, cfg, api, zones, sports, activities, venue.autoAssignSports || [], taxPct(cfg, venue));
}

// One day's bookable slots for a zone, straight from the public booking calendar (reflects real
// venue hours, slot length, closures, past times, AND existing bookings — so taken times are greyed).
interface CalSlot { startTimeUtc: string; endTimeUtc: string; startTimeLocal: string; hour: number; isAvailable: boolean;
  unavailableReason?: string | null;
  // Booking requests: the customer may ASK the venue for this time — a request-only window
  // ("League play") or a booked slot (waitlist). Absent on older API builds.
  canRequest?: boolean; requestOnlyLabel?: string | null; endTimeLocal?: string | null;
  // Sub-court capacity. Optional: an API that predates it just omits them and the widget
  // renders exactly as before.
  spotsTotal?: number; spotsLeft?: number; claimedSport?: string | null;
  // Premium hours (Domain ZonePricing): what THIS slot costs for the chosen sport, its effective
  // hourly rate, and the window's name when it is a premium hour. Absent on older API builds.
  price?: number | null; pricePerHour?: number | null; pricingLabel?: string | null;
  // Present only on the aggregated "any court" view the widget builds itself.
  zoneId?: string; }
// sportPricePerHour is the rate for ONE booking of the requested sport — the SPOT price on a split
// court. pricePerHour is the whole floor. Using the latter to quote a pickleball spot advertised $85
// for something that charges $55.
interface CalZone { zoneId: string; zoneName: string; pricePerHour: number; sportPricePerHour?: number | null }
interface BookingCalendar { days?: { slots?: CalSlot[] | null }[]; zones?: CalZone[] }

function renderForm(root: HTMLElement, cfg: EmbedConfig, api: Api, zones: BrowseZone[], sports: string[],
  activities: string[], autoAssignSports: string[] = [], pct = 0): void {
  clear(root);
  const today = new Date().toISOString().slice(0, 10);

  // Returned from a completed booking on the platform → confirm it to the customer. Strip the flags so
  // a refresh doesn't replay the banner (same pattern as the advertise widget). `order` (when the
  // platform still sends it; nothing reads it since game setup on a booking was retired, 2026-11-02).
  const q = new URLSearchParams(location.search);
  const justBooked = q.get('booking') === 'success';
  // Back from /embed-request (booking requests): confirm, then strip so a refresh doesn't replay it.
  const requestSent = q.get('request') === 'sent';
  // Funnel: tell the site tracker BEFORE the flags are stripped (single and bulk both come back as booking=success).
  if (justBooked) track('booking_success', 'booking');
  if (requestSent) track('booking_request_success', 'booking');
  if (justBooked) stripQueryParam('booking');
  if (q.get('order')) stripQueryParam('order');
  if (requestSent) stripQueryParam('request');

  /** Courts that can be booked for a sport — a basketball-only shooting lane never lists for pickleball. */
  const zonesForSport = (sport: string) =>
    zones.filter((z) => !z.allowedSports || z.allowedSports.length === 0 || z.allowedSports.indexOf(sport) >= 0);
  const zoneSel = h('select', undefined) as HTMLSelectElement;
  const sportSel = h('select', undefined,
    ...sports.map((s) => h('option', { value: s }, sportLabel(s)))) as HTMLSelectElement;
  /** Rebuild the court list for the current sport, keeping the chosen court when it still qualifies. */
  const syncZoneOptions = () => {
    const keep = zoneSel.value;
    const list = zonesForSport(sportSel.value);
    zoneSel.innerHTML = '';
    for (const z of list) zoneSel.appendChild(h('option', { value: z.id }, z.name));
    if (list.some((z) => z.id === keep)) zoneSel.value = keep;
  };
  syncZoneOptions();
  const activitySel = h('select', undefined,
    ...activities.map((a) => h('option', { value: a }, activityLabel(a)))) as HTMLSelectElement;
  // Practice is the sensible default, but a venue may not offer it — fall back to whatever is first
  // rather than leaving the select on a value it has no option for.
  activitySel.value = activities.includes('Practice') ? 'Practice' : activities[0];
  const dateInput = h('input', { type: 'date', value: today, min: today }) as HTMLInputElement;
  const slotsWrap = h('div', { class: 'e365-slots' });
  const bookBtn = h('button', { class: 'e365-btn', disabled: true }, 'Select a time') as HTMLButtonElement;
  // "$85.00 + $7.01 sales tax" right above the Book button (only when the venue taxes); the chips
  // stay at the list price and the one note beside them says the rate.
  const taxLine = h('div', { class: 'e365-muted', style: 'display:none; font-size:.82rem; margin-top:12px; text-align:center' });

  let selected: { startUtc: string; endUtc: string; zoneId?: string; price?: number | null; pricingLabel?: string | null } | null = null;
  // Per-court rates from the last calendar read, so the price shown is the SPORT's rate (a pickleball
  // spot) and not the whole floor's. zones[].bookingPricePerHour is the floor — quoting it advertised
  // $85 for something that charges $55.
  let calZones: CalZone[] = [];

  /** Does the venue pick the court for the sport currently selected? */
  const isAutoCourt = () => autoAssignSports.indexOf(sportSel.value) >= 0;

  /** Hourly rate for one booking of the chosen sport on a given court. */
  const rateFor = (zoneId: string | undefined): number | null => {
    const cz = calZones.find((z) => z.zoneId === zoneId);
    if (cz) return cz.sportPricePerHour ?? cz.pricePerHour ?? null;
    const z = zones.find((x) => x.id === zoneId);
    return z?.bookingPricePerHour ?? null;
  };

  /** The selected slot's own pre-tax price (premium hours are blended server-side); the hourly rate only as a fallback. */
  const selectedPrice = (): number | null => {
    if (selected?.price != null) return selected.price;
    return rateFor(selected?.zoneId ?? zoneSel.value);
  };
  /** Book button = the REAL total (with tax when the venue taxes), never the pre-tax list price. */
  const priceLabel = () => {
    const pre = selectedPrice();
    return pre != null ? `Book — ${money(totalWithTax(pre, pct))}` : 'Book now';
  };
  /** The breakdown line above the button; hidden when there is no tax or nothing is selected. */
  const syncTaxLine = () => {
    const pre = selectedPrice();
    const show = selected != null && pre != null && taxFor(pre, pct) > 0;
    taxLine.style.display = show ? '' : 'none';
    taxLine.textContent = show ? moneyPlusTax(pre, pct) : '';
    bookBtn.style.marginTop = show ? '6px' : '';
  };

  // Pull the REAL availability from the public booking calendar so already-booked times are shown as
  // taken (greyed) and can't be picked — no more optimistic slots that only fail at checkout. (The
  // server still enforces no-double-booking atomically; this just shows it to the customer up front.)
  const loadSlots = async () => {
    selected = null;
    bookBtn.disabled = true;
    bookBtn.textContent = 'Select a time';
    syncTaxLine();
    const date = dateInput.value;
    const auto = isAutoCourt();
    const zoneId = zoneSel.value;
    // In auto mode there is no chosen court — we ask for every court and merge them below.
    if (!date || (!auto && !zoneId)) return;
    slotsWrap.innerHTML = '';
    slotsWrap.append(h('div', { class: 'e365-muted' }, 'Loading times…'));
    let cal: BookingCalendar;
    try {
      // Sport is passed so a split court reports spots of THAT sport, and a court claimed for a
      // different sport shows as unavailable rather than bookable.
      const sportQ = sportSel.value ? `&sport=${encodeURIComponent(sportSel.value)}` : '';
      const zoneQ = auto ? '' : `&zoneId=${zoneId}`;
      cal = await api.get<BookingCalendar>(
        `/api/browse/venues/${cfg.venueId}/booking-calendar?startDate=${date}&endDate=${date}${zoneQ}&detail=full${sportQ}`);
    } catch {
      slotsWrap.innerHTML = '';
      slotsWrap.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load available times. Please try again.'));
      return;
    }
    calZones = cal.zones || [];
    let all = cal.days?.[0]?.slots || [];

    if (auto) {
      // One row per TIME, not per court: the customer is buying an hour, not a floor. A time is open
      // if ANY court can take it, and the spots shown are everything left across the building —
      // which is also why the count can exceed one court's capacity.
      const byTime = new Map<string, CalSlot>();
      for (const sl of all) {
        const key = sl.startTimeUtc;
        const prev = byTime.get(key);
        if (!prev) { byTime.set(key, { ...sl, zoneId: sl.isAvailable ? sl.zoneId : undefined }); continue; }
        const merged: CalSlot = {
          ...prev,
          isAvailable: prev.isAvailable || sl.isAvailable,
          spotsTotal: (prev.spotsTotal ?? 1) + (sl.spotsTotal ?? 1),
          spotsLeft: (prev.spotsLeft ?? 0) + (sl.spotsLeft ?? 0),
          // Keep a court that can actually serve it, for the price label. The SERVER decides the
          // real court at booking — this is only used to quote a rate.
          zoneId: prev.zoneId ?? (sl.isAvailable ? sl.zoneId : undefined),
        };
        // A reason is only meaningful once nothing anywhere is free.
        if (merged.isAvailable) merged.unavailableReason = null;
        else merged.unavailableReason = prev.unavailableReason || sl.unavailableReason;
        byTime.set(key, merged);
      }
      all = [...byTime.values()].sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc));
    }
    // If the venue has operating hours configured, the calendar marks out-of-hours slots
    // "Outside operating hours" and we trust it. If it has NO schedule (every slot open, incl.
    // overnight), fall back to a sane 6am–10pm window so we don't list 2am slots.
    const hasSchedule = all.some((s) => s.unavailableReason === 'Outside operating hours');
    // Open times (selectable) + already-booked times (greyed). Hide past/closed/outside-hours noise.
    const shown = all.filter((s) => {
      const r = s.unavailableReason || '';
      // "Running <sport>" is shown greyed like Booked — a customer should see the court is busy with
      // another sport, not just find the time silently missing.
      if (!(s.isAvailable || r === 'Booked' || r === 'Adjacent' || r === 'Request only' || s.canRequest || r.startsWith('Running '))) return false;
      if (!hasSchedule && (s.hour < 6 || s.hour > 22)) return false; // no-schedule fallback window
      return true;
    });
    slotsWrap.innerHTML = '';
    if (shown.length === 0) {
      slotsWrap.append(h('div', { class: 'e365-muted' }, 'No open times for this day.'));
      return;
    }
    for (const s of shown) {
      if (s.isAvailable) {
        const btn = h('button', { class: 'e365-slot', type: 'button',
          onClick: () => {
            selected = { startUtc: s.startTimeUtc, endUtc: s.endTimeUtc, zoneId: s.zoneId ?? zoneSel.value, price: s.price, pricingLabel: s.pricingLabel };
            slotsWrap.querySelectorAll('.e365-slot').forEach((x) => x.setAttribute('aria-pressed', 'false'));
            btn.setAttribute('aria-pressed', 'true');
            bookBtn.disabled = false;
            bookBtn.textContent = priceLabel();
            syncTaxLine();
          } }, s.startTimeLocal) as HTMLButtonElement;
        // A premium hour says so on the chip, with its price, so nobody discovers it at checkout.
        if (s.pricingLabel && s.price != null) {
          btn.appendChild(h('span', { class: 'e365-slot-note', title: s.pricingLabel }, `★ ${money(s.price)}`));
        }
        // Only on a genuinely shared court: "1 spot left" on an ordinary court would imply a scarcity
        // that does not exist.
        if ((s.spotsTotal ?? 1) > 1) {
          btn.appendChild(h('span', { class: 'e365-slot-note' },
            auto ? `${s.spotsLeft ?? 0} left` : `${s.spotsLeft ?? 0} of ${s.spotsTotal} left`));
        }
        slotsWrap.appendChild(btn);
      } else if (s.unavailableReason === 'Request only') {
        // Booking requests: the venue places this time by hand. Amber, dashed, CLICKABLE → the
        // first-party /embed-request page (same redirect model as booking itself).
        const label = s.requestOnlyLabel || 'Request only';
        const btn = h('button', { class: 'e365-slot', type: 'button',
          title: `${label} — can't be booked online. Ask the venue and they'll reach out.`,
          style: 'border:1px dashed #f59e0b;background:#fff8e1;color:#b45309',
          onClick: () => askVenue(s, 'RequestOnly', label) }, `${s.startTimeLocal} · Ask`) as HTMLButtonElement;
        slotsWrap.appendChild(btn);
      } else if (s.unavailableReason === 'Adjacent') {
        // NOT struck through and NOT labelled Booked: this time is not sold, a full session just
        // will not fit before the next booking. Painting it like a booked slot made a single 1-hour
        // reservation look like two hours of dead time to the customer.
        slotsWrap.appendChild(h('button', { class: 'e365-slot', type: 'button', disabled: true,
          title: 'Not enough time before the next booking',
          style: 'opacity:.5;border-style:dashed;cursor:not-allowed' },
          `${s.startTimeLocal} · Too short`));
      } else if (s.canRequest) {
        // Booked, but the customer may join the waitlist.
        slotsWrap.appendChild(h('button', { class: 'e365-slot', type: 'button',
          title: 'Already booked — join the waitlist and the venue will tell you if it opens up',
          style: 'opacity:.7;text-decoration:line-through',
          onClick: () => askVenue(s, 'Waitlist', null) }, `${s.startTimeLocal} · Booked`));
      } else {
        slotsWrap.appendChild(h('button', { class: 'e365-slot', type: 'button', disabled: true,
          title: 'Already booked', style: 'opacity:.45;text-decoration:line-through;cursor:not-allowed' },
          `${s.startTimeLocal} · Booked`));
      }
    }
  };

  // Booking requests: hand the slot to the first-party /embed-request page (login + the request form
  // live on 365, like /embed-book); it comes back here with ?request=sent.
  const askVenue = (s: CalSlot, kind: 'RequestOnly' | 'Waitlist', reason: string | null) => {
    if (blockIfSalesPaused(cfg, bookBtn)) return;
    const z = zones.find((x) => x.id === (s.zoneId ?? zoneSel.value));
    const day = new Date(dateInput.value + 'T00:00:00');
    const dateLabel = isNaN(day.getTime()) ? dateInput.value : day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const params = new URLSearchParams({
      venue: cfg.venueId,
      venueName: cfg.venueName || '',
      zone: s.zoneId ?? zoneSel.value,
      zoneName: z?.name || 'Court',
      start: s.startTimeUtc,
      end: s.endTimeUtc,
      label: `${dateLabel} · ${s.startTimeLocal}${s.endTimeLocal ? ' – ' + s.endTimeLocal : ''}`,
      kind,
      sport: sportSel.value,
      return: location.href.split('?')[0],
    });
    if (reason) params.set('reason', reason);
    track('booking_request_start', 'booking');
    window.location.href = withWa(`${platformBase(cfg)}/embed-request?${params.toString()}`);
  };

  // Continue to the 365 platform to sign in + pay (first-party). Carries the selection + a return URL.
  const book = () => {
    if (!selected) return;
    if (blockIfSalesPaused(cfg, bookBtn)) return;
    const auto = isAutoCourt();
    const z = zones.find((x) => x.id === zoneSel.value);
    const params = new URLSearchParams({
      venue: cfg.venueId,
      // 'auto' is the handshake: /embed-book turns it into an empty court id, and the server picks
      // the court under the booking lock — packing onto a floor already running this sport.
      zone: auto ? 'auto' : zoneSel.value,
      zoneName: auto ? 'Assigned when you book' : (z?.name || 'Court'),
      sport: sportSel.value,
      type: activitySel.value,
      start: selected.startUtc,
      end: selected.endUtc,
      return: location.href.split('?')[0], // strip any prior ?booking=success
    });
    // The slot's own amount (premium hours blended server-side) — the SPORT's rate only as a fallback.
    const amount = selected.price ?? rateFor(selected.zoneId ?? zoneSel.value);
    if (amount != null) params.set('price', String(amount));
    bookBtn.disabled = true;
    bookBtn.textContent = 'Continuing…';
    track('booking_start', 'booking');
    window.location.href = withWa(`${platformBase(cfg)}/embed-book?${params.toString()}`);
  };

  zoneSel.addEventListener('change', () => { void loadSlots(); });
  dateInput.addEventListener('change', () => { void loadSlots(); });
  // Availability is per-sport on a split court, so changing the sport must re-read it — otherwise
  // the customer picks a time that was only free for the sport they just changed away from. The same
  // is true of a recurring pattern, whose whole point is that it was checked against real
  // availability; both the sport and the activity feed that answer and its price.
  sportSel.addEventListener('change', () => { syncZoneOptions(); syncCourtField(); void loadSlots(); refreshBulkPreview(); });
  activitySel.addEventListener('change', () => { refreshBulkPreview(); });
  bookBtn.addEventListener('click', book);

  const children: (HTMLElement | string)[] = [h('h3', undefined, 'Book a court')];
  if (requestSent) {
    children.push(h('div', { class: 'e365-alert info' },
      '✓ Request sent — the venue has your details and will reach out. Nothing is reserved until they book it for you. ',
      h('a', { href: sitePage('myBookings') }, 'See it under My Bookings'), '.'));
  }
  if (justBooked) {
    children.push(h('div', { class: 'e365-alert info' },
      '✓ Booking confirmed — check your email for details. ',
      h('a', { href: sitePage('myBookings') }, 'View it under My Bookings'),
      ', or book another below.'));
    // (The "Set up your game & invite the coaches" link went with game setup on a booking, 2026-11-02.)
  }

  // ── One time vs. Weekly & multiple (bulk) mode toggle ──
  const singleWrap = h('div');
  const bulkWrap = h('div', { style: 'display:none' });
  const oneBtn = h('button', { class: 'e365-slot', type: 'button', 'aria-pressed': 'true' }, 'One time') as HTMLButtonElement;
  const bulkBtn = h('button', { class: 'e365-slot', type: 'button', 'aria-pressed': 'false' }, 'Weekly & multiple') as HTMLButtonElement;
  const setMode = (bulk: boolean) => {
    oneBtn.setAttribute('aria-pressed', String(!bulk));
    bulkBtn.setAttribute('aria-pressed', String(bulk));
    singleWrap.style.display = bulk ? 'none' : '';
    bulkWrap.style.display = bulk ? '' : 'none';
  };
  oneBtn.addEventListener('click', () => setMode(false));
  bulkBtn.addEventListener('click', () => setMode(true));

  // The court field disappears for sports the venue assigns — there is nothing to choose, and
  // leaving a dead dropdown on screen invites people to think their pick was ignored.
  const courtField = h('div', undefined, h('label', undefined, 'Court'), zoneSel);
  const autoNote = h('div', { class: 'e365-muted', style: 'font-size:.85rem; margin:2px 0 8px' },
    'Pick a time and we\u2019ll put you on a court \u2014 you\u2019ll see which one before you pay.');

  const syncCourtField = () => {
    const auto = isAutoCourt();
    courtField.style.display = auto ? 'none' : '';
    autoNote.style.display = auto ? '' : 'none';
  };
  syncCourtField();

  singleWrap.append(
    autoNote,
    h('div', { class: 'e365-row' },
      courtField,
      h('div', undefined, h('label', undefined, 'Date'), dateInput)),
    h('label', undefined, 'Available times'),
    ...[taxNoteEl(pct)].filter((n): n is HTMLElement => n != null), // "Prices plus 8.25% sales tax" (nothing when untaxed)
    slotsWrap,
    taxLine,
    bookBtn,
  );

  const refreshBulkPreview = buildBulkUi(bulkWrap, cfg, api, zones,
    () => ({ sport: sportSel.value, type: activitySel.value }), pct);

  children.push(
    h('div', { class: 'e365-slots', style: 'margin-bottom:4px' }, oneBtn, bulkBtn),
    h('div', { class: 'e365-row' },
      h('div', undefined, h('label', undefined, 'Sport'), sportSel),
      h('div', undefined, h('label', undefined, 'Type'), activitySel)),
    singleWrap,
    bulkWrap,
    h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:8px' }, 'You’ll sign in to your 365 account to confirm & pay.'),
    footer(),
  );
  root.append(...children);
  void loadSlots();
}

// ── Bulk mode: recurring patterns ("courts 1+2 every Thu 6–9pm for 8 weeks"), several patterns in
// ONE order. The server expands each pattern against real availability and returns what's bookable
// plus what was skipped (already booked / closed / outside hours) with reasons — shown before pay.
// Checkout redirects first-party to /embed-book?bulk=<encoded rules>, which re-checks and pays once.

interface BulkRule { zoneIds: string[]; weekdays: number[]; start: string; end: string; weeks: number }
interface BulkItem { zoneId: string; zoneName: string; startTimeUtc: string; endTimeUtc: string; dateLabel: string; timeLabel: string; price: number; ruleIndex: number }
interface BulkSkipped { zoneName: string; dateLabel: string; reason: string; ruleIndex: number }
interface BulkPreview { items: BulkItem[]; skipped: BulkSkipped[]; totalPrice: number; capApplied: boolean; capReason?: string | null }

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function hourLabel(value: string): string {
  const hh = parseInt(value.slice(0, 2), 10);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${value.slice(3, 5)} ${ampm}`;
}

// Returns a refresh hook: on a split court the sport decides BOTH what is available and what it
// costs, so a preview taken under a different sport has to be re-run rather than shown.
function buildBulkUi(wrap: HTMLElement, cfg: EmbedConfig, api: Api, zones: BrowseZone[],
  orderMeta: () => { sport: string; type: string }, pct = 0): () => void {
  const rules: BulkRule[] = [];
  let preview: BulkPreview | null = null;
  // Assigned once refreshPreview is in scope; returned to the caller below.
  let refreshBulk: () => void = () => {};

  // — Pattern builder controls —
  const zoneIds = new Set<string>([zones[0]?.id].filter(Boolean) as string[]);
  const zoneChips = h('div', { class: 'e365-slots' });
  const chipByZone = new Map<string, HTMLButtonElement>();
  for (const z of zones) {
    const chip = h('button', { class: 'e365-slot', type: 'button', 'aria-pressed': String(zoneIds.has(z.id)) }, z.name) as HTMLButtonElement;
    chip.addEventListener('click', () => {
      if (zoneIds.has(z.id)) zoneIds.delete(z.id); else zoneIds.add(z.id);
      chip.setAttribute('aria-pressed', String(zoneIds.has(z.id)));
    });
    chipByZone.set(z.id, chip);
    zoneChips.appendChild(chip);
  }
  /** Hide (and deselect) courts that refuse the current sport — the preview would only 400 on them. */
  const syncZoneChips = () => {
    const sport = orderMeta().sport;
    for (const z of zones) {
      const ok = !z.allowedSports || z.allowedSports.length === 0 || z.allowedSports.indexOf(sport) >= 0;
      const chip = chipByZone.get(z.id)!;
      chip.style.display = ok ? '' : 'none';
      if (!ok && zoneIds.has(z.id)) { zoneIds.delete(z.id); chip.setAttribute('aria-pressed', 'false'); }
    }
  };
  syncZoneChips();

  const weekdays = new Set<number>();
  const dayChips = h('div', { class: 'e365-slots' });
  for (let d = 1; d <= 7; d++) {
    const day = d % 7; // Mon..Sat then Sun last, matching how people book
    const chip = h('button', { class: 'e365-slot', type: 'button', 'aria-pressed': 'false' }, DAY_LABELS[day]) as HTMLButtonElement;
    chip.addEventListener('click', () => {
      if (weekdays.has(day)) weekdays.delete(day); else weekdays.add(day);
      chip.setAttribute('aria-pressed', String(weekdays.has(day)));
    });
    dayChips.appendChild(chip);
  }

  const hourOpts = Array.from({ length: 18 }, (_, i) => `${String(i + 6).padStart(2, '0')}:00`); // 6:00 AM – 11:00 PM
  const startSel = h('select', undefined, ...hourOpts.slice(0, -1).map((v) => h('option', { value: v }, hourLabel(v)))) as HTMLSelectElement;
  const endSel = h('select') as HTMLSelectElement;
  const syncEnd = () => {
    const cur = endSel.value;
    endSel.innerHTML = '';
    for (const v of hourOpts.filter((v) => v > startSel.value)) endSel.append(h('option', { value: v }, hourLabel(v)));
    if (cur && cur > startSel.value) endSel.value = cur;
  };
  startSel.value = '18:00';
  syncEnd();
  endSel.value = '21:00';
  startSel.addEventListener('change', syncEnd);

  const weeksSel = h('select', undefined, ...Array.from({ length: 26 }, (_, i) =>
    h('option', { value: String(i + 1) }, `${i + 1} week${i === 0 ? '' : 's'}`))) as HTMLSelectElement;
  weeksSel.value = '4';

  // First date the pattern may fall on. Without it the server defaults to "today at the venue", so a
  // customer could only ever start a season now - "every Tuesday from October" was impossible to say.
  // Request-level rather than per-rule, matching the API and both staff builders.
  const todayIso = (): string => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  };
  const startDateInput = h('input', { type: 'date', value: todayIso(), min: todayIso() }) as HTMLInputElement;
  startDateInput.addEventListener('change', () => {
    // The date applies to every rule already added, so any existing preview is stale.
    if (rules.length > 0) void refreshPreview();
  });

  const addBtn = h('button', { class: 'e365-btn', type: 'button' }, 'Add to order') as HTMLButtonElement;
  const builderMsg = h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:6px' });

  // — Order summary —
  const orderWrap = h('div', { style: 'margin-top:14px' });
  // Sum of the order + its tax, above the Checkout button (only when the venue taxes). The preview's
  // totalPrice is pre-tax; the tax is applied to the order total, as the server does at checkout.
  const checkoutTaxLine = h('div', { class: 'e365-muted', style: 'display:none; font-size:.82rem; margin-top:10px; text-align:center' });
  const checkoutBtn = h('button', { class: 'e365-btn', type: 'button', style: 'display:none; margin-top:10px' }, 'Checkout') as HTMLButtonElement;

  const describeRule = (r: BulkRule): string => {
    const names = r.zoneIds.map((id) => zones.find((z) => z.id === id)?.name || 'Court').join(' + ');
    const days = [...r.weekdays].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b)).map((d) => DAY_LABELS[d]).join(', ');
    return `${names} · every ${days} · ${hourLabel(r.start)}–${hourLabel(r.end)} · ${r.weeks} week${r.weeks === 1 ? '' : 's'}`;
  };

  const renderOrder = () => {
    orderWrap.innerHTML = '';
    if (rules.length === 0) { checkoutBtn.style.display = 'none'; checkoutTaxLine.style.display = 'none'; return; }
    if (!preview) return;
    orderWrap.append(h('label', undefined, 'Your order'));
    const note = taxNoteEl(pct);
    if (note) orderWrap.append(note);
    rules.forEach((r, idx) => {
      const count = preview!.items.filter((i) => i.ruleIndex === idx).length;
      const sub = preview!.items.filter((i) => i.ruleIndex === idx).reduce((s, i) => s + (i.price || 0), 0);
      const row = h('div', { class: 'e365-slot', style: 'display:flex; justify-content:space-between; align-items:center; gap:8px; cursor:default; margin-bottom:6px' });
      row.append(h('div', { style: 'text-align:left' },
        h('div', { style: 'font-weight:600; font-size:.9rem' }, describeRule(r)),
        h('div', { class: 'e365-muted', style: 'font-size:.8rem' }, `${count} booking${count === 1 ? '' : 's'} · ${money(sub)}`)));
      const rm = h('button', { class: 'e365-slot', type: 'button', title: 'Remove', style: 'flex:none' }, '✕') as HTMLButtonElement;
      rm.addEventListener('click', () => { rules.splice(idx, 1); void refreshPreview(); });
      row.append(rm);
      orderWrap.append(row);
    });
    if (preview.skipped.length) {
      const lines = preview.skipped.slice(0, 12).map((s) => `${s.dateLabel} — ${s.zoneName}: ${s.reason}`);
      if (preview.skipped.length > 12) lines.push(`…and ${preview.skipped.length - 12} more`);
      orderWrap.append(h('div', { class: 'e365-alert info', style: 'margin-top:6px' },
        h('div', { style: 'font-weight:700; margin-bottom:4px' }, `${preview.skipped.length} time${preview.skipped.length === 1 ? '' : 's'} couldn’t be added:`),
        ...lines.map((l) => h('div', { style: 'font-size:.85rem' }, l))));
    }
    if (preview.capApplied && preview.capReason)
      orderWrap.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:4px' }, preview.capReason));
    const n = preview.items.length;
    checkoutBtn.style.display = n > 0 ? '' : 'none';
    // The button carries the REAL total (with tax); the per-pattern subtotals above stay pre-tax.
    checkoutBtn.textContent = n > 0 ? `Checkout ${money(totalWithTax(preview.totalPrice, pct))} — ${n} booking${n === 1 ? '' : 's'}` : 'Checkout';
    const showTax = n > 0 && taxFor(preview.totalPrice, pct) > 0;
    checkoutTaxLine.style.display = showTax ? '' : 'none';
    checkoutTaxLine.textContent = showTax ? moneyPlusTax(preview.totalPrice, pct) : '';
    checkoutBtn.style.marginTop = showTax ? '6px' : '10px';
    if (n === 0)
      orderWrap.append(h('div', { class: 'e365-alert error', style: 'margin-top:6px' }, 'None of these times are available — try different days, times, or courts.'));
  };

  const refreshPreview = async () => {
    if (rules.length === 0) { preview = null; renderOrder(); return; }
    addBtn.disabled = true;
    builderMsg.textContent = 'Checking availability…';
    try {
      const meta = orderMeta();
      preview = await api.post<BulkPreview>(`/api/browse/venues/${cfg.venueId}/bulk-booking-preview`, {
        startDate: startDateInput.value || undefined,
        // Without these the server can only ask "is anything on this court?", which on a split floor
        // hides every spot behind the first booking and quotes the whole-court rate. Same pair is
        // sent to /embed-book, so the preview and the charge agree.
        sport: meta.sport || undefined,
        activityType: meta.type || undefined,
        rules: rules.map((r) => ({ zoneIds: r.zoneIds, weekdays: r.weekdays, startTime: r.start, endTime: r.end, weeks: r.weeks })),
      });
      builderMsg.textContent = '';
      renderOrder();
    } catch {
      builderMsg.textContent = 'We couldn’t check availability. Please try again.';
    } finally {
      addBtn.disabled = false;
    }
  };

  addBtn.addEventListener('click', () => {
    if (zoneIds.size === 0) { builderMsg.textContent = 'Pick at least one court.'; return; }
    if (weekdays.size === 0) { builderMsg.textContent = 'Pick at least one day of the week.'; return; }
    rules.push({ zoneIds: [...zoneIds], weekdays: [...weekdays], start: startSel.value, end: endSel.value, weeks: parseInt(weeksSel.value, 10) });
    void refreshPreview();
  });

  checkoutBtn.addEventListener('click', () => {
    if (rules.length === 0) return;
    if (blockIfSalesPaused(cfg, checkoutBtn)) return;
    const meta = orderMeta();
    // Compact rules payload → first-party /embed-book re-expands server-side (authoritative re-check
    // right before pay, so times taken since this preview are dropped and shown, never mis-charged).
    // startDate MUST ride along: /embed-book re-expands these rules server-side, and without it
    // the re-expansion would start today and charge for dates the customer never previewed.
    const payload = btoa(JSON.stringify({ rules, startDate: startDateInput.value || null })).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const params = new URLSearchParams({
      venue: cfg.venueId,
      sport: meta.sport,
      type: meta.type,
      bulk: payload,
      return: location.href.split('?')[0],
    });
    checkoutBtn.disabled = true;
    checkoutBtn.textContent = 'Continuing…';
    track('booking_start', 'booking');
    window.location.href = withWa(`${platformBase(cfg)}/embed-book?${params.toString()}`);
  });

  refreshBulk = () => { syncZoneChips(); void refreshPreview(); };

  wrap.append(
    h('div', { class: 'e365-muted', style: 'font-size:.85rem; margin:2px 0 8px' },
      'Build one order out of repeating times — like courts 1 + 2 every Thursday 6–9 PM, plus court 4 every Wednesday 5–7 PM. Times that are already taken get skipped and shown to you.'),
    h('label', undefined, 'Courts'), zoneChips,
    h('label', undefined, 'On these days'), dayChips,
    h('div', { class: 'e365-row' },
      h('div', undefined, h('label', undefined, 'From'), startSel),
      h('div', undefined, h('label', undefined, 'To'), endSel)),
    h('div', { class: 'e365-row' },
      h('div', undefined, h('label', undefined, 'Starting'), startDateInput),
      h('div', undefined, h('label', undefined, 'For'), weeksSel)),
    h('div', { class: 'e365-row' },
      h('div', undefined, h('label', undefined, ' '), addBtn)),
    builderMsg,
    orderWrap,
    checkoutTaxLine,
    checkoutBtn,
  );

  return refreshBulk;
}

// ── data-365="my-bookings" — the signed-in customer's court reservations at THIS venue ──
// Mirrors my-streams (login-gated, venue-filtered list). Browsing/booking stays anonymous and redirects
// to the platform; this is a read-only view of GET /api/bookings for the logged-in user. No API change.

/** A row from GET /api/bookings/orders — the order level, where refunds live. */
interface MyOrder {
  id: string;
  venueId?: string | null;
  orderNumber?: string | null;
  /** PRE-tax order total (the list price). The card was charged totalWithTax. */
  totalAmount?: number | null;
  /** Sales tax collected on top of totalAmount; 0/absent (older API) = none. */
  taxAmount?: number | null;
  /** totalAmount + taxAmount, server-computed. Absent on an older API → derived below. */
  totalWithTax?: number | null;
  cancelledAt?: string | null;
  refundRequestStatus?: string | null;   // Pending / Approved / Rejected (null = none)
  refundRequestedAt?: string | null;
  bookings?: { startTime?: string | null }[] | null;
}

/** A row from GET /api/bookings (the signed-in user's court bookings). */
interface MyBooking {
  zoneName?: string | null;
  venueId?: string | null;
  venueName?: string | null;
  startTime?: string | null;       // ISO/naive UTC (for past/future split)
  endTime?: string | null;
  startTimeLocal?: string | null;  // server-formatted "MMM d, h:mm tt" (venue tz)
  endTimeLocal?: string | null;
  // Court clear-out: the end the CUSTOMER should see ("Oct 3, 4:55 PM") when the venue tells players
  // to be off the court a few minutes early; null = show endTimeLocal. Server-resolved, incl. the
  // "not between the customer's own back-to-back hours" rule.
  courtEndTimeLocal?: string | null;
  price?: number | null;
  sport?: string | number | null;
  cancelledAt?: string | null;     // non-null => cancelled (serialization-independent of the status enum)
  orderNumber?: string | null;     // for the receipt expander
  createdAt?: string | null;       // when the order was placed (≈ paid) — receipt date
}

/** What the card was charged for an order: the server's totalWithTax, else totalAmount + taxAmount. */
function orderPaid(o: MyOrder): number {
  if (o.totalWithTax != null) return Number(o.totalWithTax) || 0;
  return Math.round(((Number(o.totalAmount) || 0) + (Number(o.taxAmount) || 0)) * 100) / 100;
}

/**
 * The tax rate an ORDER was actually charged at, from its own figures (tax ÷ pre-tax) — never the
 * venue's current rate, which may have changed since, or not applied to a free/practice order.
 * Each booking row is one slot of an order, so its tax is that slot's share at the order's rate.
 */
function orderTaxRate(o: MyOrder | undefined): number {
  const pre = Number(o?.totalAmount) || 0;
  const tax = Number(o?.taxAmount) || 0;
  return pre > 0 && tax > 0 ? (tax / pre) * 100 : 0;
}

// "MMM d, yyyy" date for the receipt (CreatedAt is ISO/naive UTC).
function fmtDateShort(utc: string): string {
  try { return asUtc(utc).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; }
}

// API times may be naive UTC ("…T13:00:00", no Z) — force UTC parsing so the past/future split is correct.
const asUtc = (s: string): Date => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
// "MMM d, h:mm tt" → just the time portion (same-day end) → "h:mm tt".
function timePart(local: string): string { const i = local.indexOf(','); return i >= 0 ? local.slice(i + 1).trim() : local; }

export function mountMyBookings(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'my-bookings') auth.clearPending();
  void renderMyBookings(root, cfg, api, auth);
}

async function renderMyBookings(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  clear(root);
  root.append(h('h3', undefined, 'My Bookings'));

  if (!auth.isLoggedIn) {
    root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Sign in to see the courts you’ve booked.'));
    const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin({ t: 'my-bookings' }));
    root.append(login, footer());
    return;
  }

  const host = h('div');
  root.append(host);
  renderLoading(host, 'My Bookings');

  let all: MyBooking[];
  let orders: MyOrder[] = [];
  try {
    // Orders carry the refund state; a failure there shouldn't hide the booking list itself.
    const [bookings, ords] = await Promise.all([
      api.get<MyBooking[]>('/api/bookings'),
      api.get<MyOrder[]>('/api/bookings/orders').catch((e) => { if (e instanceof AuthRequiredError) throw e; return [] as MyOrder[]; }),
    ]);
    all = bookings || [];
    orders = ords || [];
  } catch (e) {
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-bookings' }); return; }
    clear(host);
    host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load your bookings. Please try again.'), footer());
    return;
  }

  // This venue only — match by venueId (rename-proof); fall back to venueName for rows without one.
  const cfgVenueId = (cfg.venueId || '').toLowerCase();
  const mine = all.filter((b) => b.venueId
    ? b.venueId.toLowerCase() === cfgVenueId
    : (!cfg.venueName || b.venueName === cfg.venueName));
  clear(host);

  if (mine.length === 0) {
    host.append(
      h('div', { class: 'e365-alert info' }, 'You don’t have any bookings at this venue yet.'),
      h('a', { href: sitePage('book'), class: 'e365-btn', style: 'display:flex; text-decoration:none; margin-top:8px' }, 'Book a court'),
      signOutRow(auth.profile?.email, () => { auth.logout(); void renderMyBookings(root, cfg, api, auth); }),
      footer(),
    );
    return;
  }

  const now = Date.now();
  const endMs = (b: MyBooking) => b.endTime ? asUtc(b.endTime).getTime() : (b.startTime ? asUtc(b.startTime).getTime() + 3600000 : 0);
  const startMs = (b: MyBooking) => (b.startTime ? asUtc(b.startTime).getTime() : 0);
  const upcoming = mine.filter((b) => !b.cancelledAt && endMs(b) >= now).sort((a, b) => startMs(a) - startMs(b));
  const history = mine.filter((b) => b.cancelledAt || endMs(b) < now).sort((a, b) => startMs(b) - startMs(a));

  // Order lookup by number → the row's card can show what the card was charged (tax included).
  const orderByNumber = new Map<string, MyOrder>();
  for (const o of orders) if (o.orderNumber) orderByNumber.set(o.orderNumber, o);
  const cardFor = (b: MyBooking, dim = false) => bookingCard(b, dim, b.orderNumber ? orderByNumber.get(b.orderNumber) : undefined);

  if (upcoming.length) {
    host.append(h('label', { style: 'margin-top:0' }, 'Upcoming'));
    const list = h('div', { class: 'e365-slots', style: 'flex-direction:column' });
    upcoming.forEach((b) => list.append(cardFor(b)));
    host.append(list);
  }

  // ── Refunds — order-level (a refund covers the whole order). Shown for THIS venue's paid,
  // non-cancelled orders whose first booking is still in the future. The server enforces the real
  // policy (venue cutoff hours, confirmed-only, one pending request) — its message is surfaced as-is.
  const venueId = (cfg.venueId || '').toLowerCase();
  const refundable = orders.filter((o) =>
    (o.venueId || '').toLowerCase() === venueId &&
    !o.cancelledAt &&
    (o.totalAmount ?? 0) > 0 &&
    (o.bookings || []).some((bk) => bk.startTime && asUtc(bk.startTime).getTime() > now));
  if (refundable.length) {
    host.append(h('label', undefined, 'Refunds'));
    const list = h('div', { class: 'e365-slots', style: 'flex-direction:column' });
    for (const o of refundable) list.append(refundRow(o, cfg, api, auth, root));
    host.append(list);
  }
  if (history.length) {
    host.append(h('label', undefined, 'Past & cancelled'));
    const list = h('div', { class: 'e365-slots', style: 'flex-direction:column' });
    history.slice(0, 15).forEach((b) => list.append(cardFor(b, true)));
    host.append(list);
  }
  host.append(
    signOutRow(auth.profile?.email, () => { auth.logout(); void renderMyBookings(root, cfg, api, auth); }),
    footer(),
  );
}

function bookingCard(b: MyBooking, dim = false, order?: MyOrder): HTMLElement {
  const isCancelled = !!b.cancelledAt;
  const endLocal = b.courtEndTimeLocal || b.endTimeLocal;
  const when = b.startTimeLocal
    ? `${b.startTimeLocal}${endLocal ? ' – ' + timePart(endLocal) : ''}`
    : '';
  // This slot's share of the order's sales tax (0 when the order carried none → renders as before).
  const slotTax = b.price != null ? taxFor(b.price, orderTaxRate(order)) : 0;
  const tail = [
    typeof b.sport === 'string' && b.sport ? b.sport : null,
    isCancelled ? 'Cancelled' : (b.price != null && b.price > 0 ? money(b.price + slotTax) : 'Booked'),
  ].filter(Boolean).join(' · ');
  // Wrapper holds the bordered row + the receipt BELOW it (sibling) — identical structure to my-streams
  // so the two look the same. (Previously the receipt was nested INSIDE the bordered card → looked off.)
  const item = h('div');
  const card = h('div', { class: 'e365-slot', style: `display:block; text-align:left; cursor:default${dim ? '; opacity:.55' : ''}` },
    h('div', { style: `font-weight:600${isCancelled ? '; text-decoration:line-through' : ''}` }, `${b.zoneName || 'Court'} · ${when}`),
    h('div', { class: 'e365-muted', style: 'font-size:.8rem' }, tail),
  );
  // Court clear-out note — same wording as the 365 pages and the receipt email (BookingClearOut.CustomerNote).
  if (!isCancelled && !dim && b.courtEndTimeLocal) {
    card.append(h('div', { style: 'font-size:.78rem; color:#b45309; margin-top:4px;' },
      `Court time ends at ${timePart(b.courtEndTimeLocal)}. Please wrap up and clear the court so the next group can start on time.`));
  }
  item.append(card);
  // Receipt below the card (paid, non-cancelled only).
  if (!isCancelled && b.price != null && b.price > 0) {
    const r = receipt({
      orderNumber: b.orderNumber,
      amount: b.price,
      taxAmount: slotTax,
      paidLabel: b.createdAt ? fmtDateShort(b.createdAt) : null,
      place: [b.zoneName, b.venueName].filter(Boolean).join(' · '),
    });
    if (r) item.append(r);
  }
  return item;
}

// One order's refund row: status chip if a request exists, else a "Request refund" action.
function refundRow(o: MyOrder, cfg: EmbedConfig, api: Api, auth: Auth, root: HTMLElement): HTMLElement {
  const row = h('div', { class: 'e365-slot', style: 'display:flex; justify-content:space-between; align-items:center; gap:10px; cursor:default;' });
  const left = h('div');
  left.append(h('div', { style: 'font-weight:600;' }, `Order ${o.orderNumber || ''}`.trim()));
  // What was actually paid (tax included) — a refund covers the charged amount, not the list price.
  const tax = Number(o.taxAmount) || 0;
  left.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem;' },
    money(orderPaid(o)) + (tax > 0 ? ` (incl. ${money(tax)} sales tax)` : '')));
  row.append(left);

  const s = o.refundRequestStatus || '';
  if (s === 'Pending') {
    row.append(h('span', { class: 'e365-muted', style: 'font-size:.78rem; font-weight:700;' }, 'Refund requested — pending venue review'));
  } else if (s === 'Approved') {
    row.append(h('span', { style: 'font-size:.78rem; font-weight:700; color:#0a7d33;' }, 'Refund approved'));
  } else if (s === 'Rejected') {
    row.append(h('span', { style: 'font-size:.78rem; font-weight:700; color:#b23b00;' }, 'Refund declined'));
  } else {
    const btn = h('button', { class: 'e365-slot', type: 'button', style: 'flex:none;' }, 'Request refund') as HTMLButtonElement;
    btn.addEventListener('click', () => void requestRefund(o, cfg, api, auth, root, btn));
    row.append(btn);
  }
  return row;
}

async function requestRefund(o: MyOrder, cfg: EmbedConfig, api: Api, auth: Auth, root: HTMLElement, btn: HTMLButtonElement): Promise<void> {
  const reason = window.prompt(`Request a refund of ${money(orderPaid(o))} for order ${o.orderNumber || ''}?\n\nBriefly, what's the reason? (optional)`);
  if (reason === null) return; // cancelled the prompt
  btn.disabled = true;
  btn.textContent = 'Submitting…';
  try {
    await api.post(`/api/bookings/orders/${o.id}/refund-request`, { reason: reason.trim() || null });
    void renderMyBookings(root, cfg, api, auth); // re-render → shows the Pending chip
  } catch (e) {
    btn.disabled = false;
    btn.textContent = 'Request refund';
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'my-bookings' }); return; }
    // Surface the server's policy message (e.g. the venue's refund cutoff) instead of a generic error.
    const msg = e instanceof Error ? e.message.replace(/^365-embed API \d+:\s*/, '').replace(/^"|"$/g, '') : '';
    window.alert(msg || 'We couldn’t submit your refund request. Please try again.');
  }
}
